/**
 * The Learning Hub's per-subject artwork, so a subject looks like itself here.
 *
 * VERBATIM PORT of `SubjectSvgArt` in
 * web-frontend/components/ai-learn/HomeView.jsx. Pure SVG at low opacity, no
 * dependencies, no state — the whole point is that it is the same drawing, so
 * do not "improve" it here. If the site's art changes, re-copy it.
 *
 * Keyed by UK subject; a US subject reaches its art through SUBJECT_ART in
 * schoolPortalTheme.js, exactly as UsHomeView does through ART_FOR. A subject
 * with no art simply renders nothing, which is what the site does too — the
 * gradient alone still identifies it.
 */
import React from "react";

function SubjectSvgArt({ subjectKey, width, height }) {
  const w = width;
  const h = height;
  const c = '#fff';

  if (subjectKey === 'meteorology') {
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <path d={`M0 ${h * 0.7} Q${w * 0.15} ${h * 0.4} ${w * 0.35} ${h * 0.55} Q${w * 0.5} ${h * 0.65} ${w * 0.7} ${h * 0.45} Q${w * 0.85} ${h * 0.3} ${w} ${h * 0.5} L${w} ${h} L0 ${h} Z`} fill={c} opacity={0.04} />
        <path d={`M0 ${h * 0.85} Q${w * 0.25} ${h * 0.6} ${w * 0.5} ${h * 0.72} Q${w * 0.75} ${h * 0.85} ${w} ${h * 0.65} L${w} ${h} L0 ${h} Z`} fill={c} opacity={0.03} />
        <ellipse cx={w * 0.65} cy={h * 0.3} rx={w * 0.18} ry={h * 0.15} fill={c} opacity={0.08} />
        <ellipse cx={w * 0.75} cy={h * 0.28} rx={w * 0.14} ry={h * 0.13} fill={c} opacity={0.1} />
        <ellipse cx={w * 0.55} cy={h * 0.34} rx={w * 0.1} ry={h * 0.1} fill={c} opacity={0.06} />
        <ellipse cx={w * 0.85} cy={h * 0.38} rx={w * 0.08} ry={h * 0.08} fill={c} opacity={0.05} />
        <ellipse cx={w * 0.45} cy={h * 0.2} rx={w * 0.12} ry={h * 0.09} fill={c} opacity={0.04} />
        <circle cx={w * 0.8} cy={h * 0.15} r={3} fill={c} opacity={0.12} />
        <circle cx={w * 0.9} cy={h * 0.22} r={2} fill={c} opacity={0.08} />
        <circle cx={w * 0.5} cy={h * 0.12} r={2} fill={c} opacity={0.06} />
      </svg>
    );
  }

  if (subjectKey === 'navigation') {
    const cx = w * 0.68, cy = h * 0.48, r = h * 0.38;
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={c} strokeWidth={0.6} opacity={0.06} />
        <circle cx={cx} cy={cy} r={r * 0.7} fill="none" stroke={c} strokeWidth={0.5} opacity={0.08} />
        <circle cx={cx} cy={cy} r={r * 0.4} fill="none" stroke={c} strokeWidth={0.4} opacity={0.1} />
        <path d={`M${cx} ${cy - r} L${cx} ${cy + r}`} stroke={c} strokeWidth={0.5} opacity={0.07} />
        <path d={`M${cx - r} ${cy} L${cx + r} ${cy}`} stroke={c} strokeWidth={0.5} opacity={0.07} />
        <path d={`M${cx - r * 0.7} ${cy - r * 0.7} L${cx + r * 0.7} ${cy + r * 0.7}`} stroke={c} strokeWidth={0.3} opacity={0.05} />
        <path d={`M${cx + r * 0.7} ${cy - r * 0.7} L${cx - r * 0.7} ${cy + r * 0.7}`} stroke={c} strokeWidth={0.3} opacity={0.05} />
        <path d={`M${cx} ${cy} L${cx} ${cy - r * 0.55}`} stroke={c} strokeWidth={1.5} strokeLinecap="round" opacity={0.2} />
        <path d={`M${cx - 4} ${cy - r * 0.45} L${cx} ${cy - r * 0.6} L${cx + 4} ${cy - r * 0.45}`} fill={c} opacity={0.18} />
        <circle cx={cx} cy={cy} r={3} fill={c} opacity={0.15} />
        <path d={`M0 ${h * 0.8} Q${w * 0.3} ${h * 0.6} ${w * 0.5} ${h * 0.75} Q${w * 0.7} ${h * 0.9} ${w} ${h * 0.7}`} fill="none" stroke={c} strokeWidth={0.5} strokeDasharray="4,6" opacity={0.06} />
      </svg>
    );
  }

  if (subjectKey === 'principles-of-flight') {
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <path d={`M${w * 0.15} ${h * 0.6} Q${w * 0.35} ${h * 0.15} ${w * 0.6} ${h * 0.35} Q${w * 0.8} ${h * 0.45} ${w * 0.95} ${h * 0.55}`} fill="none" stroke={c} strokeWidth={2} opacity={0.12} />
        <path d={`M${w * 0.15} ${h * 0.6} Q${w * 0.35} ${h * 0.15} ${w * 0.6} ${h * 0.35} Q${w * 0.8} ${h * 0.45} ${w * 0.95} ${h * 0.55} L${w * 0.95} ${h * 0.6} Q${w * 0.8} ${h * 0.57} ${w * 0.6} ${h * 0.55} Q${w * 0.35} ${h * 0.52} ${w * 0.15} ${h * 0.6} Z`} fill={c} opacity={0.04} />
        <path d={`M${w * 0.15} ${h * 0.6} Q${w * 0.4} ${h * 0.55} ${w * 0.6} ${h * 0.55} Q${w * 0.8} ${h * 0.57} ${w * 0.95} ${h * 0.6}`} fill="none" stroke={c} strokeWidth={0.8} opacity={0.06} />
        {[0, 1, 2, 3, 4].map((i) => {
          const x = w * (0.4 + i * 0.1);
          const topY = h * (0.28 + i * 0.04);
          return <path key={i} d={`M${x} ${topY} L${x + 12} ${topY - 5}`} stroke={c} strokeWidth={0.5} opacity={0.06 + i * 0.01} />;
        })}
        <path d={`M${w * 0.45} ${h * 0.25} L${w * 0.45} ${h * 0.1}`} stroke={c} strokeWidth={1} opacity={0.15} />
        <path d={`M${w * 0.45 - 4} ${h * 0.14} L${w * 0.45} ${h * 0.08} L${w * 0.45 + 4} ${h * 0.14}`} fill="none" stroke={c} strokeWidth={1} opacity={0.15} />
        <path d={`M${w * 0.1} ${h * 0.85} Q${w * 0.4} ${h * 0.7} ${w * 0.7} ${h * 0.8} Q${w * 0.9} ${h * 0.88} ${w} ${h * 0.82}`} fill="none" stroke={c} strokeWidth={0.4} strokeDasharray="3,5" opacity={0.05} />
      </svg>
    );
  }

  if (subjectKey === 'aircraft-general-knowledge') {
    const cx = w * 0.7, cy = h * 0.45, r = h * 0.32;
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={c} strokeWidth={0.7} opacity={0.06} />
        <circle cx={cx} cy={cy} r={r * 0.6} fill="none" stroke={c} strokeWidth={0.5} opacity={0.08} />
        <circle cx={cx} cy={cy} r={r * 0.2} fill={c} opacity={0.08} />
        {[0, 30, 60, 90, 120, 150, 180, 169, 240, 270, 300, 330].map((a) => {
          const rad = (a * Math.PI) / 180;
          return <path key={a} d={`M${cx + Math.cos(rad) * r * 0.7} ${cy + Math.sin(rad) * r * 0.7} L${cx + Math.cos(rad) * r * 1.05} ${cy + Math.sin(rad) * r * 1.05}`} stroke={c} strokeWidth={2.5} strokeLinecap="round" opacity={0.06} />;
        })}
        <circle cx={w * 0.35} cy={h * 0.7} r={h * 0.15} fill="none" stroke={c} strokeWidth={0.4} opacity={0.04} />
        <circle cx={w * 0.35} cy={h * 0.7} r={h * 0.07} fill={c} opacity={0.03} />
        <path d={`M0 ${h * 0.9} Q${w * 0.3} ${h * 0.75} ${w * 0.6} ${h * 0.85} L${w * 0.6} ${h} L0 ${h} Z`} fill={c} opacity={0.02} />
      </svg>
    );
  }

  if (subjectKey === 'human-performance') {
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <path d={`M${w * 0.05} ${h * 0.5} Q${w * 0.15} ${h * 0.15} ${w * 0.25} ${h * 0.5} Q${w * 0.35} ${h * 0.85} ${w * 0.45} ${h * 0.5} Q${w * 0.55} ${h * 0.15} ${w * 0.65} ${h * 0.5} Q${w * 0.75} ${h * 0.85} ${w * 0.85} ${h * 0.5} Q${w * 0.95} ${h * 0.15} ${w} ${h * 0.4}`} fill="none" stroke={c} strokeWidth={1.5} opacity={0.1} />
        <path d={`M${w * 0.1} ${h * 0.6} Q${w * 0.2} ${h * 0.35} ${w * 0.3} ${h * 0.6} Q${w * 0.4} ${h * 0.85} ${w * 0.5} ${h * 0.6} Q${w * 0.6} ${h * 0.35} ${w * 0.7} ${h * 0.6} Q${w * 0.8} ${h * 0.85} ${w * 0.9} ${h * 0.6}`} fill="none" stroke={c} strokeWidth={0.8} opacity={0.06} />
        <path d={`M${w * 0.15} ${h * 0.7} Q${w * 0.25} ${h * 0.55} ${w * 0.35} ${h * 0.7} Q${w * 0.45} ${h * 0.85} ${w * 0.55} ${h * 0.7} Q${w * 0.65} ${h * 0.55} ${w * 0.75} ${h * 0.7}`} fill="none" stroke={c} strokeWidth={0.5} opacity={0.04} />
        <circle cx={w * 0.72} cy={h * 0.25} r={h * 0.18} fill="none" stroke={c} strokeWidth={0.6} opacity={0.07} />
        <circle cx={w * 0.72} cy={h * 0.25} r={h * 0.08} fill={c} opacity={0.04} />
      </svg>
    );
  }

  if (subjectKey === 'air-law') {
    const cx = w * 0.65, top = h * 0.12;
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <path d={`M${cx} ${top} L${cx} ${h * 0.82}`} stroke={c} strokeWidth={1.2} opacity={0.08} />
        <path d={`M${cx - w * 0.2} ${h * 0.28} L${cx + w * 0.2} ${h * 0.28}`} stroke={c} strokeWidth={1.2} opacity={0.1} />
        <path d={`M${cx - w * 0.2} ${h * 0.28} L${cx - w * 0.25} ${h * 0.55} L${cx - w * 0.15} ${h * 0.55} Z`} fill={c} opacity={0.05} stroke={c} strokeWidth={0.5} />
        <path d={`M${cx + w * 0.2} ${h * 0.28} L${cx + w * 0.15} ${h * 0.55} L${cx + w * 0.25} ${h * 0.55} Z`} fill={c} opacity={0.05} stroke={c} strokeWidth={0.5} />
        <path d={`M${cx - w * 0.08} ${h * 0.82} L${cx + w * 0.08} ${h * 0.82}`} stroke={c} strokeWidth={2.5} strokeLinecap="round" opacity={0.08} />
        <circle cx={cx} cy={top} r={4} fill={c} opacity={0.12} />
        <path d={`M0 ${h * 0.9} Q${w * 0.2} ${h * 0.75} ${w * 0.4} ${h * 0.88} Q${w * 0.6} ${h} ${w * 0.8} ${h * 0.9}`} fill="none" stroke={c} strokeWidth={0.4} opacity={0.04} />
      </svg>
    );
  }

  if (subjectKey === 'flight-performance-planning') {
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <path d={`M${w * 0.3} ${h * 0.9} L${w * 0.3} ${h * 0.1} L${w * 0.95} ${h * 0.1}`} fill="none" stroke={c} strokeWidth={0.5} opacity={0.06} />
        {[0.3, 0.5, 0.7].map((y) => (
          <path key={y} d={`M${w * 0.3} ${h * y} L${w * 0.95} ${h * y}`} stroke={c} strokeWidth={0.3} strokeDasharray="2,4" opacity={0.04} />
        ))}
        <path d={`M${w * 0.3} ${h * 0.85} Q${w * 0.45} ${h * 0.6} ${w * 0.55} ${h * 0.55} Q${w * 0.7} ${h * 0.48} ${w * 0.8} ${h * 0.2}`} fill="none" stroke={c} strokeWidth={1.8} strokeLinecap="round" opacity={0.14} />
        <path d={`M${w * 0.3} ${h * 0.85} Q${w * 0.45} ${h * 0.6} ${w * 0.55} ${h * 0.55} Q${w * 0.7} ${h * 0.48} ${w * 0.8} ${h * 0.2} L${w * 0.8} ${h * 0.9} L${w * 0.3} ${h * 0.9} Z`} fill={c} opacity={0.03} />
        <circle cx={w * 0.55} cy={h * 0.55} r={3} fill={c} opacity={0.18} />
        <circle cx={w * 0.8} cy={h * 0.2} r={4} fill={c} opacity={0.2} />
      </svg>
    );
  }

  if (subjectKey === 'communications') {
    const cx = w * 0.6, cy = h * 0.45;
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        {[h * 0.15, h * 0.25, h * 0.35, h * 0.45].map((r, i) => (
          <path key={i} d={`M${cx + r} ${cy - r * 0.7} A${r} ${r * 0.7} 0 0 1 ${cx + r} ${cy + r * 0.7}`} fill="none" stroke={c} strokeWidth={1.2} strokeLinecap="round" opacity={0.05 + i * 0.02} />
        ))}
        <circle cx={cx} cy={cy} r={4} fill={c} opacity={0.12} />
        <path d={`M${cx} ${cy + 5} L${cx} ${h * 0.88}`} stroke={c} strokeWidth={1.2} opacity={0.08} />
        <path d={`M${cx - 8} ${h * 0.88} L${cx + 8} ${h * 0.88}`} stroke={c} strokeWidth={1} strokeLinecap="round" opacity={0.06} />
        <path d={`M0 ${h * 0.85} Q${w * 0.15} ${h * 0.7} ${w * 0.3} ${h * 0.82} Q${w * 0.4} ${h * 0.92} ${w * 0.5} ${h * 0.85}`} fill="none" stroke={c} strokeWidth={0.4} opacity={0.04} />
      </svg>
    );
  }

  if (subjectKey === 'operational-procedures') {
    const lx = w * 0.58, ly = h * 0.08;
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
        <path d={`M${lx} ${ly} L${w * 0.92} ${ly} L${w * 0.92} ${h * 0.92} L${lx} ${h * 0.92} Z`} fill={c} opacity={0.03} stroke={c} strokeWidth={0.5} />
        <path d={`M${lx + (w * 0.92 - lx) * 0.3} ${ly} L${lx + (w * 0.92 - lx) * 0.3} ${ly - 6} L${lx + (w * 0.92 - lx) * 0.7} ${ly - 6} L${lx + (w * 0.92 - lx) * 0.7} ${ly}`} fill="none" stroke={c} strokeWidth={0.5} opacity={0.08} />
        {[0.22, 0.38, 0.54, 0.7].map((pct, i) => {
          const y = h * pct;
          return (
            <React.Fragment key={i}>
              <path d={`M${lx + 18} ${y} L${w * 0.85} ${y}`} stroke={c} strokeWidth={0.4} opacity={0.04} />
              {i < 3 ? (
                <path d={`M${lx + 6} ${y - 2} L${lx + 9} ${y + 1} L${lx + 13} ${y - 3}`} fill="none" stroke={c} strokeWidth={1.2} strokeLinecap="round" opacity={0.16} />
              ) : (
                <circle cx={lx + 9.5} cy={y} r={2.5} fill="none" stroke={c} strokeWidth={0.7} opacity={0.1} />
              )}
            </React.Fragment>
          );
        })}
      </svg>
    );
  }

  // Default
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <path d={`M0 ${h * 0.7} Q${w * 0.3} ${h * 0.4} ${w * 0.6} ${h * 0.6} Q${w * 0.8} ${h * 0.7} ${w} ${h * 0.5} L${w} ${h} L0 ${h} Z`} fill={c} opacity={0.03} />
      <circle cx={w * 0.7} cy={h * 0.35} r={h * 0.25} fill="none" stroke={c} strokeWidth={0.6} opacity={0.06} />
      <circle cx={w * 0.55} cy={h * 0.5} r={h * 0.12} fill={c} opacity={0.03} />
    </svg>
  );
}

export default SubjectSvgArt;
